import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { getJournalPost } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

export default function JournalArticle() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setData(null);
        setNotFound(false);
        getJournalPost(slug)
            .then(setData)
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <main className="bg-[#F6F1E7] min-h-screen pt-40 pb-24 text-center">
                <h1 className="font-serif-display text-4xl">Article not found.</h1>
                <Link to="/journal" className="btn-luxe btn-green mt-8 inline-flex">Back to Journal</Link>
            </main>
        );
    }

    if (!data) {
        return (
            <main className="bg-[#F6F1E7] min-h-screen pt-32 pb-24 px-5">
                <div className="max-w-[760px] mx-auto space-y-6">
                    <Skeleton className="h-80 rounded-xl bg-black/[0.06]" />
                    <Skeleton className="h-12 w-3/4 bg-black/[0.06]" />
                    <Skeleton className="h-48 bg-black/[0.06]" />
                </div>
            </main>
        );
    }

    const { post, more } = data;

    return (
        <main className="bg-[#F6F1E7] min-h-screen pb-24" data-testid="journal-article-page">
            <section className="relative h-[55vh] min-h-[400px] overflow-hidden flex items-end">
                <img src={post.hero_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/85 via-[#0B0B0C]/25 to-[#0B0B0C]/40" />
                <div className="relative z-10 max-w-[860px] mx-auto px-5 sm:px-8 pb-14 w-full">
                    <p className="font-mono-meta text-[10px] tracking-[0.25em] text-[#C9B37A]">
                        {post.category.toUpperCase()} · {post.read_time.toUpperCase()}
                    </p>
                    <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#F6F1E7] mt-4 leading-tight" data-testid="journal-article-title">
                        {post.title}
                    </h1>
                </div>
            </section>

            <article className="max-w-[720px] mx-auto px-5 sm:px-8 pt-16">
                <Reveal>
                    <p className="font-serif-display text-2xl italic text-black/70 leading-snug border-l-2 border-[#C9B37A] pl-6">
                        {post.excerpt}
                    </p>
                </Reveal>
                <div className="mt-12 space-y-7">
                    {post.body.map((para, i) => (
                        <Reveal key={i} delay={0.05}>
                            <p className="text-base sm:text-lg text-black/70 leading-relaxed">{para}</p>
                        </Reveal>
                    ))}
                </div>
            </article>

            {more?.length > 0 && (
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 mt-24">
                    <Reveal className="text-center mb-10">
                        <h2 className="font-serif-display text-3xl font-medium">Keep reading</h2>
                    </Reveal>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                        {more.map((p) => (
                            <Link key={p.id} to={`/journal/${p.slug}`} className="group block" data-testid={`journal-more-${p.slug}`}>
                                <div className="rounded-xl overflow-hidden">
                                    <img src={p.hero_image} alt={p.title} loading="lazy" className="w-full aspect-[4/3] object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                                </div>
                                <p className="font-mono-meta text-[9px] tracking-[0.22em] text-[#8C6A3D] mt-4">{p.category.toUpperCase()}</p>
                                <h3 className="font-serif-display text-xl font-medium mt-2 leading-snug group-hover:text-[#0F2A1F] transition-colors">{p.title}</h3>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </main>
    );
}
